import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { customerFacingSiteName } from '@aot-aiops/shared';
import { AssetVisual } from './asset-visuals';
import {
  Activity,
  Bot,
  ChevronRight,
  Clock3,
  GitBranch,
  Maximize2,
  MoreHorizontal,
  RefreshCw,
  Save,
  Trash2,
  Wrench,
} from 'lucide-react';
import {
  buildTopology,
  deriveHealthFactors,
  filterOperationalEvents,
  freshnessLabel,
  relationshipPath,
  safeSavedFilters,
  type HealthFactor,
  type OperationalEvent,
  type TimelineCategory,
  type TopologyNodeModel,
} from './intelligence';
import {
  EmptyState,
  HealthIndicator,
  PageHeader,
  PlatformBadge,
  SectionCard,
  StatusBadge,
  formatDate,
  statusTone,
} from './components';
import { customerFacingEntityName } from './presentation';

export function FreshnessIndicator({
  value,
  state,
  onRefresh,
}: {
  value?: string | null;
  state?: string | null;
  onRefresh?: () => void;
}) {
  const text = freshnessLabel(value);
  const liveLabel = state === 'paused' ? 'Paused' : state === 'stale' ? 'Stale' : 'Live';
  return (
    <span className={`freshness-indicator tone-${statusTone(state)}`}>
      <Clock3 size={14} aria-hidden="true" />
      <span>{liveLabel} · {state === 'stale' || state === 'offline' ? `${text} · Data may be stale` : text}</span>
      {onRefresh && (
        <button onClick={onRefresh} aria-label="Refresh current view" title="Refresh current view">
          <RefreshCw size={14} aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

export function HealthBreakdown({
  health,
  factors,
  label = 'Why is this unhealthy?',
}: {
  health?: string;
  factors?: HealthFactor[];
  label?: string;
}) {
  const items = factors ?? deriveHealthFactors({ health });
  return (
    <details className="health-breakdown">
      <summary>
        <HealthIndicator status={health} />
        <span>{health === 'healthy' ? 'Why is this healthy?' : label}</span>
        <ChevronRight size={15} aria-hidden="true" />
      </summary>
      <div>
        <p>Contributing facts already reported by the platform</p>
        <ul>
          {items.map((item, index) => (
            <li key={`${item.label}-${index}`}>
              <i className={`tone-${statusTone(item.status)}`} aria-hidden="true" />
              <span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </span>
            </li>
          ))}
        </ul>
        <small>No new health score or threshold is calculated in this view.</small>
      </div>
    </details>
  );
}

export function IncidentFactStrip({
  incident,
  primary,
  siteName,
  durationLabel,
}: {
  incident: any;
  primary?: any;
  siteName: string;
  durationLabel: string;
}) {
  return (
    <div className="incident-fact-strip" aria-label="Primary incident facts">
      <div>
        <span>Severity</span>
        <StatusBadge value={incident.severity} />
      </div>
      <div>
        <span>Status</span>
        <StatusBadge value={incident.status} />
      </div>
      <div>
        <span>Asset</span>
        <strong>{primary?.displayName ?? 'Not reported'}</strong>
      </div>
      <div>
        <span>Site</span>
        <strong>{siteName || '—'}</strong>
      </div>
      <div>
        <span>Detected</span>
        <strong>{formatDate(incident.opened_at)}</strong>
      </div>
      <div>
        <span>Duration</span>
        <strong>{durationLabel}</strong>
      </div>
      <div>
        <span>Acknowledgement</span>
        <strong>{incident.acknowledged_at ? 'Acknowledged' : 'Pending'}</strong>
      </div>
      <div>
        <span>Recovery</span>
        <strong>{incident.recovered_at ? 'Recovered' : 'Active'}</strong>
      </div>
    </div>
  );
}

const timelineCategories: TimelineCategory[] = [
  'Incidents',
  'Maintenance',
  'Agents',
  'Capacity',
  'Configuration',
];

export function OperationalTimeline({
  events,
  title = 'Operational timeline',
  siteId,
  onAskOpsAI,
  onOpenEvent,
}: {
  events: OperationalEvent[];
  title?: string;
  siteId?: string;
  onAskOpsAI?: () => void;
  onOpenEvent?: (event: OperationalEvent) => void;
}) {
  const [range, setRange] = useState(1);
  const [categories, setCategories] = useState<TimelineCategory[]>(timelineCategories);
  const scoped = useMemo(
    () => events.filter((event) => !siteId || !event.siteId || event.siteId === siteId),
    [events, siteId],
  );
  const visible = filterOperationalEvents(scoped, range, categories).slice(0, 40);
  function toggle(category: TimelineCategory) {
    setCategories((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category],
    );
  }
  return (
    <SectionCard
      className="operational-timeline-card"
      title={title}
      description="Meaningful operational events, not raw telemetry samples."
      action={
        onAskOpsAI ? (
          <button className="text-button" onClick={onAskOpsAI}>
            Overnight summary <Bot size={15} />
          </button>
        ) : undefined
      }
    >
      <div className="timeline-controls">
        <div className="segmented-control" aria-label="Timeline range">
          <button className={range === 1 ? 'active' : ''} onClick={() => setRange(1)}>
            24h
          </button>
          <button className={range === 7 ? 'active' : ''} onClick={() => setRange(7)}>
            7d
          </button>
        </div>
        <div className="timeline-filters" aria-label="Timeline event filters">
          {timelineCategories.map((category) => (
            <button
              key={category}
              className={categories.includes(category) ? 'active' : ''}
              aria-pressed={categories.includes(category)}
              onClick={() => toggle(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>
      {visible.length ? (
        <ol className="operational-timeline">
          {visible.map((event) => (
            <li key={event.id} className={`tone-${statusTone(event.status)}`}>
              <i aria-hidden="true" />
              <button
                className="timeline-event"
                disabled={!event.target || !onOpenEvent}
                onClick={() => onOpenEvent?.(event)}
                aria-label={`Open context for ${event.title}`}
              >
                <span>{event.category}</span>
                <strong>{event.title}</strong>
                <p>{event.detail}</p>
                <time dateTime={event.occurredAt}>{formatDate(event.occurredAt)}</time>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState
          title="No matching operational events"
          description={`No selected event categories were reported during the last ${range === 1 ? '24 hours' : '7 days'}.`}
        />
      )}
    </SectionCard>
  );
}

export function RelationshipBreadcrumb({
  siteName,
  assets,
  assetId,
  onSelect,
}: {
  siteName: string;
  assets: any[];
  assetId: string;
  onSelect?: (id: string) => void;
}) {
  const path = relationshipPath(assets, assetId);
  return (
    <nav className="relationship-breadcrumb" aria-label="Asset relationship path">
      <span>{siteName}</span>
      {path.map((asset) => (
        <span key={asset.id}>
          <ChevronRight size={13} aria-hidden="true" />
          {onSelect ? (
            <button onClick={() => onSelect(asset.id)}>{customerFacingEntityName(asset.displayName ?? asset.name, asset.source === 'demo')}</button>
          ) : (
            <strong>{customerFacingEntityName(asset.displayName ?? asset.name, asset.source === 'demo')}</strong>
          )}
        </span>
      ))}
    </nav>
  );
}

function platformFor(item: any) {
  const source = String(item.source ?? '').toLowerCase();
  if (source !== 'demo') return source;
  const type = String(item.assetType ?? item.asset_type ?? '');
  if (type.startsWith('proxmox')) return 'proxmox';
  if (type.startsWith('vmware')) return 'vmware';
  if (type.startsWith('hyperv')) return 'hyperv';
  return 'demo';
}

function TopologyNode({
  node,
  selected,
  onSelect,
}: {
  node: TopologyNodeModel;
  selected?: string;
  onSelect: (id: string) => void;
}) {
  const item = node.asset;
  const assetType = item.assetType ?? item.kind ?? item.source ?? 'asset';
  return (
    <li>
      <button
        className={selected === item.id ? 'selected' : ''}
        onClick={() => onSelect(item.id)}
        aria-label={`${customerFacingEntityName(item.displayName ?? item.name, item.source === 'demo')}, ${assetType}, ${item.health ?? 'unknown'}`}
      >
        <span>
          <AssetVisual asset={item} size={24} status={item.health} />
          <strong>{customerFacingEntityName(item.displayName ?? item.name, item.source === 'demo')}</strong>
        </span>
        <small>{assetType}</small>
        <span className="topology-node-meta">
          <PlatformBadge source={platformFor(item)} />
          <StatusBadge value={item.health} />
        </span>
      </button>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <TopologyNode
              key={child.asset.id}
              node={child}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function TopologyView({
  assets,
  sites,
  selectedAssetId,
  onSelectAsset,
  onOpenAsset,
  onNavigate,
  serviceName,
  relationships = [],
}: {
  assets: any[];
  sites: any[];
  selectedAssetId?: string;
  onSelectAsset: (id?: string) => void;
  onOpenAsset: (id: string) => void;
  onNavigate: (view: string, question?: string) => void;
  serviceName?: string;
  relationships?: any[];
}) {
  const selectedAsset = assets.find((asset) => asset.id === selectedAssetId);
  const initialSite = selectedAsset?.siteId ?? assets[0]?.siteId ?? sites[0]?.id ?? '';
  const [siteId, setSiteId] = useState(initialSite);
  const initialMode =
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('mode');
  const [mode, setMode] = useState(
    serviceName
      ? 'Service'
      : ['Infrastructure', 'Network', 'Impact', 'Dependencies'].includes(initialMode ?? '')
        ? initialMode!
        : 'Infrastructure',
  );
  useEffect(() => {
    if (selectedAsset?.siteId) setSiteId(selectedAsset.siteId);
  }, [selectedAsset?.siteId]);
  const siteAssets = assets.filter((asset) => asset.siteId === siteId);
  const byId = new Map(siteAssets.map((asset) => [asset.id, asset]));
  const withAncestors = (ids: Set<string>) => {
    for (const id of [...ids]) {
      let current = byId.get(id);
      while (current?.parentAssetId && byId.has(current.parentAssetId)) {
        ids.add(current.parentAssetId);
        current = byId.get(current.parentAssetId);
      }
    }
    return ids;
  };
  let visibleAssets = siteAssets;
  if (mode === 'Impact') {
    const impacted = withAncestors(
      new Set(
        siteAssets
          .filter((asset) =>
            ['critical', 'warning', 'offline', 'stale'].includes(asset.health ?? asset.status),
          )
          .map((asset) => asset.id),
      ),
    );
    visibleAssets = siteAssets.filter((asset) => impacted.has(asset.id));
  } else if (mode === 'Dependencies' && selectedAssetId) {
    const related = withAncestors(new Set([selectedAssetId]));
    for (const asset of siteAssets)
      if (asset.parentAssetId === selectedAssetId) related.add(asset.id);
    visibleAssets = siteAssets.filter((asset) => related.has(asset.id));
  }
  const roots = buildTopology(visibleAssets, siteId);
  const selectedSite = sites.find((site) => site.id === siteId);
  const selectedChildren = selectedAsset
    ? assets.filter((asset) => asset.parentAssetId === selectedAsset.id)
    : [];
  return (
    <>
      <PageHeader
        eyebrow="INFRASTRUCTURE RELATIONSHIPS"
        title="Topology"
        description={
          serviceName
            ? `Service-scoped dependency context for ${serviceName}, built only from modeled components and reported inventory relationships.`
            : 'Site relationships based on the available parent and child inventory links.'
        }
        actions={
          <label className="topology-site-select">
            <span className="sr-only">Topology site</span>
            <select
              value={siteId}
              onChange={(event) => {
                setSiteId(event.target.value);
                onSelectAsset(undefined);
              }}
            >
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {customerFacingSiteName(site)}
                </option>
              ))}
            </select>
          </label>
        }
      />
      <div className="topology-toolbar" aria-label="Topology view mode">
        {['Infrastructure', 'Network', 'Impact', 'Dependencies', ...(serviceName ? ['Service'] : [])].map(
          (item) => (
            <button
              key={item}
              className={mode === item ? 'active' : ''}
              aria-pressed={mode === item}
              onClick={() => {
                setMode(item);
                const params = new URLSearchParams(window.location.search);
                if (item === 'Infrastructure') params.delete('mode');
                else params.set('mode', item);
                window.history.replaceState(
                  null,
                  '',
                  `/topology${params.size ? `?${params}` : ''}`,
                );
              }}
            >
              {item}
            </button>
          ),
        )}
        <span>
          {mode === 'Network'
            ? `${relationships.filter((item) => item.siteId === siteId).length} evidence-backed links`
            : mode === 'Impact'
            ? 'Only reported issues and their dependency path'
            : `${visibleAssets.length} entities in scope`}
        </span>
      </div>
      <div className={`topology-layout ${selectedAsset ? 'has-selection' : ''}`}>
        <SectionCard
          className="topology-canvas"
          title={selectedSite ? customerFacingSiteName(selectedSite) : 'Site topology'}
          description={`${visibleAssets.length} operational entities · ${mode} view`}
          action={
            <button
              className="text-button"
              onClick={() =>
                document.querySelector('.topology-tree')?.scrollTo({ left: 0, top: 0 })
              }
            >
              Fit to view <Maximize2 size={15} />
            </button>
          }
        >
          {roots.length ? (
            <div className="topology-scroll" tabIndex={0} aria-label="Infrastructure topology">
              <div className="site-root">
                <Activity size={16} aria-hidden="true" />
                <strong>{selectedSite ? customerFacingSiteName(selectedSite) : ''}</strong>
                <small>Site</small>
              </div>
              <ul className="topology-tree">
                {roots.map((node) => (
                  <TopologyNode
                    key={node.asset.id}
                    node={node}
                    selected={selectedAssetId}
                    onSelect={onSelectAsset}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <EmptyState
              title="No relationship data for this site"
              description="Assets appear here only when existing inventory contains a real site or parent relationship. No hierarchy is inferred."
            />
          )}
          {mode === 'Network' && relationships.filter((item) => item.siteId === siteId).length > 0 && (
            <div className="topology-link-evidence">
              {relationships.filter((item) => item.siteId === siteId).slice(0, 40).map((item) => (
                <span key={item.id}><strong>{item.relationshipType}</strong> · {item.source} · {item.confidence.replaceAll('_', ' ').toLowerCase()} · observed {formatDate(item.lastObservedAt)}</span>
              ))}
            </div>
          )}
        </SectionCard>
        <SectionCard className="topology-context" title="Selected context">
          {selectedAsset ? (
            <>
              <RelationshipBreadcrumb
                siteName={selectedSite ? customerFacingSiteName(selectedSite) : 'Site'}
                assets={assets}
                assetId={selectedAsset.id}
                onSelect={onSelectAsset}
              />
              <div className="topology-selected-header">
                <div>
                  <PlatformBadge source={platformFor(selectedAsset)} />
                  <StatusBadge value={selectedAsset.health} />
                </div>
                <h2>{selectedAsset.displayName ?? selectedAsset.name}</h2>
                <p>{selectedAsset.assetType}</p>
              </div>
              <dl className="topology-facts">
                <div>
                  <dt>Status</dt>
                  <dd>{selectedAsset.status}</dd>
                </div>
                <div>
                  <dt>Parent</dt>
                  <dd>
                    {assets.find((asset) => asset.id === selectedAsset.parentAssetId)
                      ?.displayName ?? 'Site root'}
                  </dd>
                </div>
                <div>
                  <dt>Children</dt>
                  <dd>{selectedChildren.length}</dd>
                </div>
                <div>
                  <dt>Last seen</dt>
                  <dd>{formatDate(selectedAsset.lastSeenAt)}</dd>
                </div>
              </dl>
              <div className="context-action-grid">
                <button onClick={() => onOpenAsset(selectedAsset.id)}>Open asset</button>
                <button onClick={() => onNavigate('Incidents')}>View incidents</button>
                <button onClick={() => onNavigate('Capacity')}>View capacity</button>
                <button
                  onClick={() =>
                    onNavigate(
                      'OpsAI',
                      `Summarize the operational context for ${selectedAsset.displayName ?? selectedAsset.name}.`,
                    )
                  }
                >
                  Ask OpsAI
                </button>
                <button onClick={() => onNavigate('Settings')}>Maintenance</button>
              </div>
            </>
          ) : (
            <EmptyState
              title="Select an asset"
              description="Choose a keyboard-reachable node to inspect its real parent, children, health, and safe actions."
            />
          )}
        </SectionCard>
      </div>
    </>
  );
}

type SavedView = { id: string; name: string; filters: Record<string, string> };

export function SavedViewMenu({
  storageKey,
  filters,
  onApply,
}: {
  storageKey: string;
  filters: Record<string, string>;
  onApply: (filters: Record<string, string>) => void;
}) {
  const [items, setItems] = useState<SavedView[]>([]);
  const [name, setName] = useState('');
  useEffect(() => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) ?? '[]');
      if (Array.isArray(value)) setItems(value.slice(0, 8));
    } catch {
      setItems([]);
    }
  }, [storageKey]);
  function persist(next: SavedView[]) {
    setItems(next);
    localStorage.setItem(storageKey, JSON.stringify(next.slice(0, 8)));
  }
  function save() {
    const cleanName = name.trim().slice(0, 40);
    const savedFilters = safeSavedFilters(filters);
    if (!cleanName || !Object.keys(savedFilters).length) return;
    persist(
      [{ id: crypto.randomUUID(), name: cleanName, filters: savedFilters }, ...items].slice(0, 8),
    );
    setName('');
  }
  return (
    <details className="saved-view-menu">
      <summary>
        <Save size={14} aria-hidden="true" /> My views
      </summary>
      <div>
        <p>Saved only in this browser. Free-text searches are excluded.</p>
        <label>
          <span className="sr-only">Saved view name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="View name"
          />
        </label>
        <button className="button secondary" onClick={save} disabled={!name.trim()}>
          Save current filters
        </button>
        {items.length ? (
          <ul>
            {items.map((item) => (
              <li key={item.id}>
                <button onClick={() => onApply(item.filters)}>{item.name}</button>
                <button
                  aria-label={`Delete saved view ${item.name}`}
                  onClick={() => persist(items.filter((view) => view.id !== item.id))}
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <small>No personal saved views yet.</small>
        )}
      </div>
    </details>
  );
}

export function ContextActionMenu({
  children,
  label = 'Context actions',
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <details className="context-action-menu">
      <summary aria-label={label} title={label}>
        <MoreHorizontal size={16} />
      </summary>
      <div>{children}</div>
    </details>
  );
}

export function MaintenanceAction({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick}>
      <Wrench size={14} /> Maintenance
    </button>
  );
}

export function TopologyAction({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick}>
      <GitBranch size={14} /> Topology
    </button>
  );
}
